import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Boxes, ChevronDown, ChevronRight, CheckCircle2, Save, Info } from "lucide-react";
import { getBoqSkuMapping, upsertBoqSkuMapping } from "../../services/interiorExecution";
import { searchCategories, searchBrands } from "../../services/materialAdmin";

/**
 * Pre-tender BOQ → SKU/brand mapping (v3_54, structured in v3_61).
 *
 * The ID authors the BOQ as free-text specs. Before floating tenders, the BV associate maps each
 * spec line to a structured Category → Sub-category and Brand (from our masters) plus a concrete
 * SKU/product code. The sub-category is written onto the BOQ line and drives tender vendor-matching;
 * the brand is stored on the mapping. A line is "mapped" once it has a SKU + a sub-category.
 */
const cellSelect =
  "w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-flamingo-500 focus:border-flamingo-500 bg-white";

const BoqSkuMappingPanel = ({ leadId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Collapsed by default — mapping is a one-time pre-tender step, so it stays tucked away
  // (expand to edit) and keeps the cost breakdown / plan the first thing the user sees.
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set());
  // draft edits keyed by boq line id: { categoryId, subcategoryId, brandId, sku }
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  // Material masters (loaded once).
  const [topCategories, setTopCategories] = useState([]); // [{ value, label }]
  const [subcatsByParent, setSubcatsByParent] = useState({}); // parentId -> [{ value, label }]
  const [brandOptions, setBrandOptions] = useState([]); // [{ value, label, categoryIds: [] }]

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catRes, brandRes] = await Promise.all([
          searchCategories({ pageSize: 500, withAttributes: false }),
          searchBrands({ pageSize: 500, withCategories: true }),
        ]);
        if (cancelled) return;
        const cats = catRes?.items || (Array.isArray(catRes) ? catRes : []);
        setTopCategories(
          cats.filter((c) => c.parentCategoryId == null).map((c) => ({ value: c.materialCategoryId, label: c.categoryName }))
        );
        const byParent = {};
        cats
          .filter((c) => c.parentCategoryId != null)
          .forEach((c) => {
            (byParent[c.parentCategoryId] = byParent[c.parentCategoryId] || []).push({
              value: c.materialCategoryId,
              label: c.categoryName,
            });
          });
        setSubcatsByParent(byParent);
        const brands = brandRes?.items || (Array.isArray(brandRes) ? brandRes : []);
        setBrandOptions(
          brands.map((b) => ({
            value: b.materialBrandId,
            label: b.brandName,
            categoryIds: (b.categories || []).map((c) => c.materialCategoryId),
          }))
        );
      } catch (e) {
        if (!cancelled) { setTopCategories([]); setSubcatsByParent({}); setBrandOptions([]); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBoqSkuMapping(leadId);
      setData(res);
      setOpenGroups(new Set((res?.groups || []).map((g) => g.packageKey)));
      const seed = {};
      (res?.groups || []).forEach((g) =>
        (g.lines || []).forEach((l) => {
          seed[l.interiorDesignerBoqId] = {
            categoryId: l.materialCategoryId ?? "",
            subcategoryId: l.materialSubcategoryId ?? "",
            brandId: l.materialBrandId ?? "",
            // SKU/product is optional — default to the designer's material description; BV refines
            // only if they want to pin a specific product.
            sku: l.skuText || l.material || "",
          };
        })
      );
      setDrafts(seed);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleGroup = (key) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const setDraft = (lineId, patch) =>
    setDrafts((prev) => ({ ...prev, [lineId]: { ...prev[lineId], ...patch } }));

  const saveLine = async (line) => {
    const d = drafts[line.interiorDesignerBoqId] || {};
    if (!d.subcategoryId) {
      toast.error("Pick a category and sub-category (needed to match vendors)");
      return;
    }
    if (!d.brandId) {
      toast.error("Pick a brand");
      return;
    }
    const brand = brandOptions.find((b) => b.value === Number(d.brandId));
    setSavingId(line.interiorDesignerBoqId);
    try {
      await upsertBoqSkuMapping(leadId, line.interiorDesignerBoqId, {
        // SKU is optional — null lets the backend default to the designer's material description.
        skuText: (d.sku || "").trim() || null,
        materialCategoryId: d.categoryId ? Number(d.categoryId) : null,
        materialSubcategoryId: Number(d.subcategoryId),
        materialBrandId: Number(d.brandId),
        brandText: brand?.label || null,
      });
      toast.success("Mapping saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save mapping");
    } finally {
      setSavingId(null);
    }
  };

  const total = data?.totalLines ?? 0;
  const mapped = data?.mappedLines ?? 0;
  const allMapped = !!data?.allMapped;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white mb-4 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <Boxes size={18} className="text-flamingo-600" />
          <span className="font-semibold text-slate-900">Material SKU Mapping</span>
          <span className="text-xs text-slate-500">(assign category &amp; brand before floating tenders)</span>
        </div>
        <div className="flex items-center gap-3">
          {total > 0 && (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                allMapped
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }`}
            >
              {allMapped && <CheckCircle2 size={12} />}
              {mapped}/{total} mapped
            </span>
          )}
          {open ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5">
          {loading ? (
            <p className="text-sm text-slate-500 py-4">Loading…</p>
          ) : !data || total === 0 ? (
            <p className="text-sm text-slate-500 py-4">
              No approved BOQ to map yet. Mapping becomes available once a design is developer-approved.
            </p>
          ) : (
            <>
              {!allMapped && (
                <div className="mb-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                  <Info size={14} className="text-amber-600 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Each item needs a category, sub-category &amp; brand before tenders can be floated. {total - mapped} remaining.
                  </p>
                </div>
              )}
              {(data.groups || []).map((g) => {
                const gOpen = openGroups.has(g.packageKey);
                return (
                  <div key={g.packageKey} className="border border-slate-200 rounded-xl mb-2 overflow-hidden">
                    <button
                      onClick={() => toggleGroup(g.packageKey)}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 text-left"
                    >
                      <span className="text-sm font-semibold text-slate-800">{g.label || g.packageKey}</span>
                      <span className="text-xs text-slate-500">
                        {g.mappedCount}/{g.lineCount} · {gOpen ? "hide" : "show"}
                      </span>
                    </button>
                    {gOpen && (
                      <div className="divide-y divide-slate-100">
                        {(g.lines || []).map((l) => {
                          const d = drafts[l.interiorDesignerBoqId] || {};
                          const subOpts = d.categoryId ? subcatsByParent[Number(d.categoryId)] || [] : [];
                          const brandOpts = d.categoryId
                            ? brandOptions.filter((b) => b.categoryIds.includes(Number(d.categoryId)))
                            : [];
                          return (
                            <div key={l.interiorDesignerBoqId} className="px-4 py-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                              <div className="md:col-span-3 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{l.material || "—"}</p>
                                <p className="text-xs text-slate-500 truncate">
                                  {[l.room, l.workItem].filter(Boolean).join(" · ")}
                                  {l.quantity != null ? ` · qty ${l.quantity}${l.unit ? ` ${l.unit}` : ""}` : ""}
                                </p>
                              </div>
                              {/* Category */}
                              <select
                                value={d.categoryId || ""}
                                onChange={(e) =>
                                  setDraft(l.interiorDesignerBoqId, { categoryId: e.target.value, subcategoryId: "", brandId: "" })
                                }
                                className={`md:col-span-2 ${cellSelect}`}
                              >
                                <option value="">Category…</option>
                                {topCategories.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                              {/* Sub-category */}
                              <select
                                value={d.subcategoryId || ""}
                                onChange={(e) => setDraft(l.interiorDesignerBoqId, { subcategoryId: e.target.value })}
                                disabled={!d.categoryId}
                                className={`md:col-span-2 ${cellSelect} disabled:bg-slate-50 disabled:text-slate-400`}
                              >
                                <option value="">{d.categoryId ? "Sub-category…" : "Pick category"}</option>
                                {subOpts.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                              {/* Brand */}
                              <select
                                value={d.brandId || ""}
                                onChange={(e) => setDraft(l.interiorDesignerBoqId, { brandId: e.target.value })}
                                disabled={!d.categoryId}
                                className={`md:col-span-2 ${cellSelect} disabled:bg-slate-50 disabled:text-slate-400`}
                              >
                                <option value="">{d.categoryId ? "Brand…" : "Pick category"}</option>
                                {brandOpts.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                              {/* SKU + save */}
                              <div className="md:col-span-3 flex items-center gap-2">
                                <input
                                  value={d.sku || ""}
                                  onChange={(e) => setDraft(l.interiorDesignerBoqId, { sku: e.target.value })}
                                  placeholder="Product / SKU (optional)"
                                  className="flex-1 min-w-0 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-flamingo-500 focus:border-flamingo-500"
                                />
                                {l.isMapped && <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />}
                                <button
                                  onClick={() => saveLine(l)}
                                  disabled={savingId === l.interiorDesignerBoqId}
                                  className="inline-flex items-center gap-1 rounded-lg bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5 shrink-0"
                                >
                                  <Save size={13} /> Save
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BoqSkuMappingPanel;
